import { useCallback, useState } from 'react';
import { billingApi } from '../services/api';

/**
 * useBillingPortal — the Stripe Billing Portal session flow, once (task 1015).
 *
 * Every Stripe portal door in Settings does the same three things: create a
 * portal session, redirect the browser to the URL Stripe returns, and say
 * something useful when that fails. Task 1015 moved those doors onto the pages
 * that own the concern — Change plan onto Subscription, Update payment method
 * and Edit billing details onto Payment — so the wiring lives here rather than
 * being copied into each page.
 *
 * `flow` deep links into the portal's subscription-update or
 * payment-method-update flow (task 941); omitted lands on the portal home,
 * which is where billing details are edited.
 *
 * The return URL stays `/settings/billing?billing_return=1` — the historical
 * Stripe return URL.
 */
export function useBillingPortal() {
  const [redirecting, setRedirecting] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const openPortal = useCallback(async (flow?: 'plan' | 'payment_method') => {
    setRedirecting(true);
    setSessionError(null);

    const returnUrl = `${window.location.origin}/settings/billing?billing_return=1`;

    try {
      const response = await billingApi.createSession({
        mode: 'portal',
        returnUrl,
        ...(flow ? { flow } : {}),
      });
      window.location.href = response.data.data.url;
    } catch (err: unknown) {
      const e = err as { status?: number };
      if (e?.status === 404 || e?.status === 501 || !e?.status) {
        setSessionError('Billing management is not available yet. Please contact support.');
      } else {
        setSessionError('Something went wrong. Please try again.');
      }
      setRedirecting(false);
    }
  }, []);

  return { openPortal, redirecting, sessionError };
}
